// src/App.js
import "@/App.css";
// Icon served from public/icons/ — no bundled import needed.
// Previously imported from src/assets/images/icon-512.png which was not committed
// to git, breaking every Cloudflare build with ENOENT.
const appIcon = '/icons/icon-512.png';
import { lazy, Suspense, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import ErrorBoundary        from "@/components/error/ErrorBoundary";
import ContextErrorBoundary from "@/components/error/ContextErrorBoundary";
import { ThemeProvider }               from "@/contexts/ThemeContext";
import { PinProvider, usePin }         from "@/contexts/PinContext";
import { AuthProvider, useAuth }       from "@/contexts/AuthContext";
import { ProfileProvider, useProfile } from "@/contexts/ProfileContext";
import { useFeatureUnlock } from "@/hooks/feature/useFeatureUnlock";
import { SyncStatusProvider }          from "@/contexts/SyncStatusContext";
import PinScreen          from "@/components/auth/PinScreen";
import LoginScreen        from "@/components/auth/LoginScreen";
import ProfileSetupScreen from "@/components/auth/ProfileSetupScreen";
import Layout             from "@/components/layout/Layout";
import TriggerEngineProvider from "@/components/layout/TriggerEngineProvider";
import UpdateBanner           from "@/components/feedback/UpdateBanner";
import MemoryVerseReminder    from "@/components/feature/ScriptureMemoryReminder";

const Dashboard           = lazy(() => import("@/pages/Dashboard"));
const JournalEntry        = lazy(() => import("@/pages/JournalEntry"));
const PeopleKeeper       = lazy(() => import("@/pages/PeopleKeeper"));
const ExpenseLedger       = lazy(() => import("@/pages/ExpenseLedger"));
const DiscipleshipKeeper = lazy(() => import("@/pages/DiscipleshipKeeper"));
const BibleStudyGuide     = lazy(() => import("@/pages/BibleStudyGuide"));
const EvangelismGuide     = lazy(() => import("@/pages/EvangelismGuide"));
const Stewardship         = lazy(() => import("@/pages/Stewardship"));
const Reports             = lazy(() => import("@/pages/Reports"));
const Settings            = lazy(() => import("@/pages/Settings"));
const CalendarEvents      = lazy(() => import("@/pages/CalendarEvents"));
const PrayerJournal       = lazy(() => import("@/pages/PrayerJournal"));
const SermonNotes         = lazy(() => import("@/pages/SermonNotes"));
const GrowthHub           = lazy(() => import("@/pages/GrowthHub"));
const Bible               = lazy(() => import("@/pages/Bible"));
const DataTools           = lazy(() => import("@/pages/DataTools"));
const MemoryVerse         = lazy(() => import("@/pages/ScriptureMemory"));
const PublicPrayerRequest = lazy(() => import("@/pages/PublicPrayerRequest"));

// ── Platform detection (respects user override) ────────────────────────────
// Now reads from IDB for consistency after theme migration.
(async function detectPlatform() {
  try {
    const { idbGet } = await import('@/lib/db/storageAdapter');
    const pref = (await idbGet('platformMode')) || 'auto';
    if (pref === 'ios' || pref === 'android') {
      document.documentElement.setAttribute('data-platform', pref);
      return;
    }
    const ua = navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    document.documentElement.setAttribute('data-platform', isIOS ? 'ios' : 'android');
  } catch {
    // Fallback to auto-detect if IDB fails
    const ua = navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    document.documentElement.setAttribute('data-platform', isIOS ? 'ios' : 'android');
  }
})();

// ── Apply persisted display preferences immediately ─────────────────────────
// Now reads from IDB via async call to avoid flicker after migration.
(async function applyPersistedPrefs() {
  try {
    const { idbGet } = await import('@/lib/db/storageAdapter');
    const el = document.documentElement;
    el.setAttribute('data-ui-scale',    (await idbGet('uiScale')) || 'default');
    el.setAttribute('data-line-spacing', (await idbGet('lineSpacing')) || 'default');
    el.setAttribute('data-btn-scale',   (await idbGet('buttonScale')) || 'default');
  } catch {
    // Fallback to defaults if IDB fails
  }
})();

// ── Invite link: /join/<prayer code> ───────────────────────────────────────
// Remember the code, then continue at "/" so the normal sign-up / login flow runs.
// useInviteLink (mounted in Layout through useSharedPrayerSync) links the new user
// to the inviter once they are signed in and have finished profile setup.
(function captureInvite() {
  try {
    if (typeof window === 'undefined') return;
    const m = window.location.pathname.match(/^\/join\/([A-Za-z0-9-]{8,32})\/?$/);
    if (!m) return;
    localStorage.setItem('sheepheard:pendingInvite', m[1].toUpperCase().replace(/[^A-Z0-9]/g, ''));
    window.history.replaceState({}, '', '/');
  } catch { /* storage unavailable */ }
})();

// ── Android App Links: https://disciplesheep.space/join/<code> opens the installed app ──
// The web page never loads in that case, so read the code from the native URL-open event
// (both when the app is already running and on a cold start from the link).
if (typeof window !== 'undefined') {
  import('@capacitor/core').then(({ Capacitor }) => {
    if (!Capacitor.isNativePlatform()) return;
    import('@capacitor/app').then(({ App: CapacitorApp }) => {
      const handleUrl = (url) => {
        const m = String(url || '').match(/\/join\/([A-Za-z0-9-]{8,32})\/?(?:[?#].*)?$/);
        if (!m) return;
        try {
          localStorage.setItem('sheepheard:pendingInvite', m[1].toUpperCase().replace(/[^A-Z0-9]/g, ''));
          window.dispatchEvent(new Event('sheepheard:invite'));
        } catch { /* storage unavailable */ }
      };
      CapacitorApp.addListener('appUrlOpen', ({ url }) => handleUrl(url));
      // Android re-delivers the same launch intent every time it restarts the app (RouteMemory exists for
      // that reason), so a launch URL is only handled once per 24h. Fresh taps arrive via appUrlOpen above.
      CapacitorApp.getLaunchUrl().then((res) => {
        const url = res?.url;
        if (!url) return;
        try {
          const seen = JSON.parse(localStorage.getItem('sheepheard:lastLaunchUrl') || 'null');
          if (seen?.url === url && Date.now() - seen.at < 24 * 60 * 60 * 1000) return;
          localStorage.setItem('sheepheard:lastLaunchUrl', JSON.stringify({ url, at: Date.now() }));
        } catch { /* storage unavailable */ }
        handleUrl(url);
      }).catch(() => {});
    }).catch(() => {});
  }).catch(() => {});
}

function Splash() {
  return (
    <div className="flex flex-col justify-center items-center min-h-screen bg-gradient-to-b from-green-900 via-stone-900 to-stone-800 gap-4">
      <img src={appIcon} alt="Sheepheard" className="w-24 h-24 rounded-2xl object-contain shadow-lg" />
      <p className="font-serif font-extrabold text-2xl text-white m-0">Sheepheard</p>
      <p className="text-white/60 text-xs italic m-0 text-center max-w-[280px] leading-relaxed">
        &quot;My sheep hear my voice, and I know them, and they follow me.&quot; — John 10:27
      </p>
      <div className="flex gap-2 mt-4">
        {[0, 0.2, 0.4].map(d => (
          <div key={d} className="w-2 h-2 rounded-full bg-white/60 animate-pulse" style={{ animationDelay: `${d}s` }} />
        ))}
      </div>
    </div>
  );
}

const PageLoader = () => (
  <div className="flex justify-center items-center min-h-[60vh]">
    <div className="animate-pulse opacity-60 text-neutral-600">Loading…</div>
  </div>
);

// ── 404 fallback ──────────────────────────────────────────────────────────────
function NotFound() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3 p-8 text-center">
      <p className="text-5xl m-0">🐑</p>
      <p className="text-lg font-bold m-0">Page not found</p>
      <p className="text-sm text-stone-500 m-0">
        This path doesn&apos;t exist. Head back to the dashboard.
      </p>
      <a href="/" className="mt-2 px-5 py-2.5 rounded-lg bg-green-700 text-white font-bold text-sm no-underline">
        Go to Dashboard
      </a>
    </div>
  );
}

// ── FIX #3: Role-based route guard ────────────────────────────────────────────
// Must live INSIDE AppContent's render tree so useProfile() is available.
// Redirects to "/" (Dashboard) until the current path is unlocked by level progression
// (useFeatureUnlock). `feature` is only a truthy flag now — any value enables the check.
function RoleRoute({ feature, children }) {
  const { isUnlocked, ready: levelDataReady } = useFeatureUnlock();
  const location = useLocation();

  // FIX (Flag 48): extraFeatures (role-based) and level-progression (useFeatureUnlock)
  // were two independent gates on the same routes — a route reachable by role but
  // not yet level-unlocked (or vice versa) could be reached by direct navigation,
  // bypassing whichever system Dashboard's tap-handler happened to check. Both must
  // now agree before a role-gated route renders.
  // Level progression is the single gate. The old role-based extraFeatures
  // check was removed: it blocked 11 of 16 roles from /stewardship/* even
  // though Level 3-4 tasks (tithe, budget, missionary support) require it.
  const hasLevelAccess = isUnlocked(location.pathname.replace(/(.)\/+$/, '$1'));

  // FIX (RoleRoute race): completedChallenges/storedLevel load asynchronously,
  // so effectiveLevel defaults to 0 on first render — making hasLevelAccess
  // always false before that data resolves. Redirecting on that stale false
  // bounced every level-gated route back to "/" on first load, regardless of
  // the user's real level. Hold off the redirect decision until ready.
  if (feature && !levelDataReady) {
    return <PageLoader />;
  }

  if (feature && !hasLevelAccess) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// Remounts ProfileProvider whenever the signed-in account changes so the profile is
// reloaded for that uid instead of keeping the previous account's in-memory copy.
function KeyedProfileProvider({ children }) {
  const { user } = useAuth();
  return <ProfileProvider key={user?.uid ?? 'anon'}>{children}</ProfileProvider>;
}

// ── Reopen the last page after Android restarts the app ──────────────────────
// A cold start always begins at "/". Save the current route and, on a cold start
// that lands on "/", go back to the saved one (if it is recent enough).
const ROUTE_KEY = 'sheepheard:lastRoute';
const ROUTE_MAX_AGE_MS = 2 * 60 * 60 * 1000;

function RouteMemory() {
  const location = useLocation();
  const navigate = useNavigate();
  const restored = useRef(false);

  useEffect(() => {
    if (!restored.current) {
      restored.current = true;
      if (location.pathname === '/') {
        try {
          const saved = JSON.parse(localStorage.getItem(ROUTE_KEY) || 'null');
          if (saved?.path && saved.path !== '/' && Date.now() - saved.at < ROUTE_MAX_AGE_MS) {
            navigate(saved.path, { replace: true });
            return;
          }
        } catch { /* storage unavailable */ }
      }
    }
    try {
      localStorage.setItem(ROUTE_KEY, JSON.stringify({ path: location.pathname + location.search, at: Date.now() }));
    } catch { /* storage unavailable */ }
  }, [location, navigate]);

  return null;
}

function AppContent() {
  const { isUnlocked, hasPin, loading: pinLoading } = usePin();
  const { isAuthenticated, authLoading }             = useAuth();
  const { hasCompletedSetup, profileReady, profileTimedOut } = useProfile();

  // FIX #4: Warn user if profile data failed to load from IDB in time.
  useEffect(() => {
    if (profileTimedOut) {
      toast.warning('Some data may not have loaded. Check your storage settings.');
    }
  }, [profileTimedOut]);

  if (pinLoading || authLoading || !profileReady) return <Splash />;
  if (hasPin && !isUnlocked)  return <PinScreen />;
  if (!isAuthenticated)       return <LoginScreen />;
  if (!hasCompletedSetup)     return <ProfileSetupScreen />;

  // ThemeProvider is now above this component in the tree — no wrapper needed here.
  return (
    <TriggerEngineProvider>
    <div className="App">
      <UpdateBanner />
      <RouteMemory />
      <MemoryVerseReminder />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index                                 element={<Dashboard />} />
            <Route path="journal"                        element={<JournalEntry />} />
            <Route path="calendar"                       element={<RoleRoute feature="level"><CalendarEvents /></RoleRoute>} />
            <Route path="discipleship"                   element={<RoleRoute feature="level"><DiscipleshipKeeper /></RoleRoute>} />
            <Route path="discipleship/bible-study-guide" element={<RoleRoute feature="level"><BibleStudyGuide /></RoleRoute>} />
            <Route path="evangelism"                     element={<RoleRoute feature="level"><EvangelismGuide /></RoleRoute>} />
            <Route path="prayer"                         element={<PrayerJournal />} />
            <Route path="people"                         element={<RoleRoute feature="level"><PeopleKeeper /></RoleRoute>} />
            <Route path="bible"                          element={<Bible />} />
            <Route path="bible/reading-plan"             element={<Navigate to="/bible" replace />} />
            <Route path="settings"                       element={<Settings />} />
            <Route path="growth"                        element={<GrowthHub />} />
            <Route path="challenges"                     element={<Navigate to="/growth" replace />} />

            {/* Level-gated routes — RoleRoute redirects to "/" until the path is unlocked by level */}
            <Route path="sermon-notes" element={
              <RoleRoute feature="sermonNotes"><SermonNotes /></RoleRoute>
            } />
            <Route path="stewardship" element={
              <RoleRoute feature="stewardship"><Stewardship /></RoleRoute>
            } />
            <Route path="stewardship/people" element={
              <RoleRoute feature="stewardship"><PeopleKeeper /></RoleRoute>
            } />
            <Route path="stewardship/expenses" element={
              <RoleRoute feature="stewardship"><ExpenseLedger /></RoleRoute>
            } />
            <Route path="stewardship/reports" element={
              <RoleRoute feature="stewardship"><Reports /></RoleRoute>
            } />

            <Route path="data-tools"                     element={<DataTools />} />
            <Route path="memory-verse"                   element={<MemoryVerse />} />

            {/* FIX: 404 catch-all — renders inside Layout so nav stays visible */}
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </Suspense>
      <Toaster position="top" richColors visibleToasts={1} expand={false}
        offset="var(--safe-area-inset-top, env(safe-area-inset-top, 0px))"
        mobileOffset={{ top: 'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))' }} />
    </div>
    </TriggerEngineProvider>
  );
}

export default function App() {
  // Public prayer-request page: bypass every provider (no PIN, auth, profile,
  // theme, or Layout) so logged-out visitors can open /pray/<token>.
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/pray/')) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <PublicPrayerRequest />
        </Suspense>
      </ErrorBoundary>
    );
  }
  return (
    <ErrorBoundary>
      {/*
        ThemeProvider is now the outermost context — directly inside ErrorBoundary.
        This ensures applyTheme() and applyFontSize() fire on first mount, before
        Splash, PinScreen, LoginScreen, or ProfileSetupScreen are ever rendered.
        Previously it was buried inside AppContent, so pre-auth screens never
        received the correct theme class on <html>.
      */}
      <PinProvider>
        <ContextErrorBoundary>
          {/*
            ThemeProvider is inside PinProvider because ThemeProvider calls
            useSyncStore → usePin(). Mounting it outside PinProvider throws
            "usePin must be used inside <PinProvider>".
            Theme still applies before Splash/PinScreen because ThemeProvider
            fires applyTheme() in a useEffect on its first render, which
            happens synchronously before any child paints.
          */}
          <ThemeProvider>
            <ContextErrorBoundary>
              <AuthProvider>
                <ContextErrorBoundary>
                  <KeyedProfileProvider>
                    {/* SyncStatusProvider: polls IDB pendingQueue (no Firestore reads).
                        Lives inside ProfileProvider so auth/profile are available
                        to future status extensions. Outside BrowserRouter so the
                        banner can render on any route including pre-router screens. */}
                    <SyncStatusProvider>
                    {/* BrowserRouter MUST be here — outside AppContent so that
                        Layout's useLocation/NavLink/Outlet all share one router
                        instance for the entire app lifetime. */}
                    <BrowserRouter>
                      <AppContent />
                    </BrowserRouter>
                    </SyncStatusProvider>
                  </KeyedProfileProvider>
                </ContextErrorBoundary>
              </AuthProvider>
            </ContextErrorBoundary>
          </ThemeProvider>
        </ContextErrorBoundary>
      </PinProvider>
    </ErrorBoundary>
  );
}